<script setup lang="ts">

import {computed, ref} from "vue";
import {useEndpoint} from "@/common/utils/fetch";

const postbackEndpoint = useEndpoint("/teacher/students/clever")

type CleverStudent = {
  cleverId: string
  cleverName: string
  grade: string
  userId: number | null
  userName: string | null
  teacherId: number | null
  teacherName: string | null
  selected: boolean
}

const props = defineProps<{
  teacherId: number
  students: CleverStudent[]
}>()

const belongsToAnotherTeacher = (student: CleverStudent) => student.teacherId && student.teacherId !== props.teacherId

const candidates = ref(props.students.filter(student => !belongsToAnotherTeacher(student)))
const others = ref(props.students.filter(student => belongsToAnotherTeacher(student)))

candidates.value.forEach(student => student.selected = student.teacherId === props.teacherId)

function hasDifferentUsername(student: CleverStudent) {
  return student.userName && student.userName !== student.cleverName
}

const allSelected = computed(() => candidates.value.every(student => student.selected))

const selectedStudents = computed(() =>
  candidates.value.filter(student => student.selected)
    .concat(others.value.filter(student => student.selected))
)

function selectAll() {
  candidates.value.forEach(student => student.selected = true)
}

function deselectAll() {
  candidates.value.forEach(student => student.selected = false)
}

function toggleSelectAll() {
  if (allSelected.value) {
    deselectAll()
  } else {
    selectAll()
  }
}

async function importStudents() {
  const students = selectedStudents.value
  const response = await postbackEndpoint.post({students}).execute()
  const data = await response.json()
  if (data.redirect) {
    window.location.href = data.redirect
  }
}

</script>

<template>
  <div class="import-clever-students">
    <h1>Import Clever Students</h1>
    <table>
      <thead>
      <tr>
        <th>
          <input type="checkbox" readonly :checked="allSelected" @click="toggleSelectAll"/>
          <span class="select-all-label">Select All</span></th>
        <th>Name</th>
        <th>Grade</th>
        <th>Notes</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="student in candidates" :key="student.cleverId">
        <td>
          <input v-model="student.selected" type="checkbox"/>
        </td>
        <td>
          <div>{{ student.cleverName }}</div>
          <div v-if="hasDifferentUsername(student)" class="name-differs">
            (L2L name: {{ student.userName }})
          </div>
        </td>
        <td>{{ student.grade }}</td>
        <td>
          <div v-if="student.teacherId === teacherId">Student already imported</div>
        </td>
      </tr>
      </tbody>
    </table>

    <!--    <pre>{{ { candidates, selectedStudents, allSelected } }}</pre>-->

    <div v-if="others.length" class="others">
      <h2>Students assigned to other teachers</h2>
      <div class="warning">
        If you import any of these students, you will remove them from their teacher's current classroom
        and add them to yours.<br>
        <em>Please check with their current teacher before doing this.</em>
      </div>
      <table>
        <thead>
        <tr>
          <th>&nbsp; <!-- select all not available for these students --></th>
          <th>Name</th>
          <th>Grade</th>
          <th>Teacher Name</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="student in others" :key="student.cleverId">
          <td>
            <input v-model="student.selected" type="checkbox"/>
          </td>
          <td>
            <div>{{ student.cleverName }}</div>
            <div v-if="student.userName !== student.cleverName" class="name-differs">
              (L2L name: {{ student.userName }})
            </div>
          </td>
          <td>{{ student.grade }}</td>
          <td>{{ student.teacherName }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <button v-if="selectedStudents.length" class="import-students-button" @click="importStudents">
      Import Selected Students
    </button>

  </div>
</template>

<style lang="scss" scoped>

// There's a lot more style here than needs to be because bootstrap 4 is still broken in Inertia pages

.import-clever-students {
  max-width: 800px;
  margin: 3rem;
  padding: 2rem;

}

table, th, td {
  border: 1px solid #888;
}

th, td {
  padding: 0.5rem;
}

input[type=checkbox] {
  transform: scale(1.8);
  margin-left: 1rem;
}

.select-all-label {
  margin-left: 1rem;
  font-size: 0.8rem;
  font-weight: normal;
}

.name-differs {
  font-size: 0.8em;
  color: #555;
  font-style: italic;
}

.others {
  margin-top: 2rem;
}

.warning {
  width: 75%;
  margin: 1rem 0;
  background-color: #ffcccc;
  border-color: black;
  border-radius: 1rem;
  border-width: 2px;
  padding: 1rem;
  font-weight: bold;
}

.import-students-button {
  color: white;
  font-size: 1.2rem;
  background-color: forestgreen;
  border: 1px solid #888;
  border-radius: 1rem;
  margin-top: 1rem;
  padding: 0.5rem 1rem;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.25) 0 4px 8px;
    transform: translateY(-2px);
  }
}
</style>
